import { ReactElement, useEffect, useRef } from 'react';
import { Button } from 'components';
import { PictureInput } from '../PictureInput';
import { ToggleSwitchMini } from 'components/ToggleSwitch';
import { Form, FormFooterContainer } from './styles';
import { SelectClientType } from '../GroupRadioInput';
import {
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { EditClientFormValues } from './types';
import { useTranslation } from 'react-i18next';
import { defaultFormValues } from './constants';
import { ControlledInput } from '../ControlledInput';

interface FormContentProps {
  defaultValues: EditClientFormValues;
  disabled: boolean;
  onCancel: () => void;
  onSubmit: (data: EditClientFormValues) => void;
}

export function FormContent({
  defaultValues,
  disabled,
  onCancel,
  onSubmit,
}: Readonly<FormContentProps>): ReactElement {
  const firstRender = useRef(false);
  const methods = useForm<EditClientFormValues>({
    defaultValues: defaultFormValues,
  });

  useEffect(() => {
    if (defaultValues && !firstRender.current) {
      firstRender.current = true;
      methods.reset(defaultValues);
    }
  }, [defaultValues]);

  return (
    <FormProvider {...methods}>
      <Form onSubmit={methods.handleSubmit(onSubmit)}>
        <ControlledInput name="name" label="Nome" disabled={disabled} />
        <ControlledInput name="email" label="E-mail" disabled={disabled} />
        <ControlledInput
          name="phone"
          label="Telefone"
          mask="phone"
          disabled={disabled}
        />
        <ControlledInput
          name="cnpj"
          label="CNPJ"
          mask="cnpj"
          disabled={disabled}
        />
        <PictureInput picture={defaultValues.picture} disabled={disabled} />
        <SelectClientType disabled={disabled} />
        <ToggleSwitchInput disabled={disabled} />
        <FormFooter onCancel={onCancel} disabled={disabled} />
      </Form>
    </FormProvider>
  );
}

function ToggleSwitchInput({
  disabled,
}: Readonly<{ disabled: boolean }>): ReactElement {
  const { control, setValue } = useFormContext<EditClientFormValues>();
  const { t } = useTranslation();

  const enabledWatch = useWatch<EditClientFormValues, 'enabled'>({
    control,
    name: 'enabled',
  });

  return (
    <ToggleSwitchMini
      label={t(enabledWatch ? 'habilitado' : 'desabilitado')}
      checked={enabledWatch}
      variant="default"
      onClick={() => {
        setValue('enabled', !enabledWatch);
      }}
      disabled={disabled}
    />
  );
}

function FormFooter({
  disabled,
  onCancel,
}: Readonly<{
  disabled: boolean;
  onCancel: () => void;
}>): ReactElement {
  const { t } = useTranslation();

  return (
    <FormFooterContainer>
      <Button
        type="button"
        variant="neutral-ghost"
        onClick={onCancel}
        disabled={disabled}
      >
        {t('cancelar')}
      </Button>
      <Button type="submit" variant="primary-filled" disabled={disabled}>
        {t('salvar')}
      </Button>
    </FormFooterContainer>
  );
}
